import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { UncontrolledTooltip } from 'reactstrap';
import _ from 'lodash';

import { buttonsV2 as Buttons, Icon } from '@breezeway/brzw-components';
import Avatar from '../avatar';
import { personName } from '../../utils/people_helpers';
import { detectOutOfViewport } from '../../utils/viewport';

import './assignees_bar.scss';

const CLASS_NAME = 'assignees-bar';

class AssigneesBar extends Component {
  constructor(props) {
    super(props);

    this.ref = React.createRef();

    this.state = {
      dropdownPosition: 'right',
    };

    this.renderAvatar = this.renderAvatar.bind(this);
  }

  componentDidMount() {
    const isDropdownOutOfScreen = this.isOutOfViewport();
    if (isDropdownOutOfScreen) {
      this.setState({
        dropdownPosition: 'left',
      });
    }
  }

  onRemovePerson(id) {
    return () => {
      this.props.onRemove(id);
    };
  }

  isOutOfViewport() {
    return _.some(detectOutOfViewport(this.ref, 'assignees-bar__dropdown'));
  }

  renderAvatar(person) {
    const { name } = this.props;
    const personId = _.get(person, 'id');
    const personFullName = personName(person);
    const id = `${name}-person-${personId}`;

    return (
      <div
        className={`${CLASS_NAME}__avatar-wrapper`}
        id={id}
        key={id}
        onClick={this.onRemovePerson(personId)}
        role="presentation"
      >
        <Avatar
          className={`${CLASS_NAME}__avatar ${CLASS_NAME}__avatar--removable`}
          person={person}
          size="xxsmall"
        />
        <UncontrolledTooltip
          className={`${CLASS_NAME}__avatar-tooltip`}
          placement="top"
          target={id}
        >
          {personFullName}
        </UncontrolledTooltip>
      </div>
    );
  }

  renderMorePeople(morePeople) {
    const { dropdownPosition } = this.state;
    const morePeopleSize = _.size(morePeople);

    return (
      <div className={`${CLASS_NAME}__more-people`}>
        <Avatar
          className={`${CLASS_NAME}__avatar ${CLASS_NAME}__avatar--more`}
          size="xxsmall"
          text={`+${morePeopleSize}`}
        />
        <div className={`${CLASS_NAME}__dropdown ${CLASS_NAME}__dropdown--${dropdownPosition}`}>
          {_.map(morePeople, person => {
            const personId = _.get(person, 'id');
            const personFullName = personName(person);

            return (
              <div className={`${CLASS_NAME}__dropdown-item`} key={`dropdown-${personId}`}>
                <Avatar className={`${CLASS_NAME}__avatar`} person={person} size="xxsmall" />
                <div
                  className={`${CLASS_NAME}__assignee-name ${CLASS_NAME}__assignee-name--in-dropdown`}
                >
                  {personFullName}
                </div>
                <div role="presentation" onClick={this.onRemovePerson(personId)}>
                  <Icon className={`${CLASS_NAME}__remove`} iconName="cancel" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderPeopleSection() {
    const { actions, onAdd, people } = this.props;

    const shouldShowMore = _.size(people) > 4;
    const firstPeople = _.slice(people, 0, shouldShowMore ? 3 : 4);
    const morePeople = _.slice(people, 3);
    const isOneAssignee = _.isEqual(_.size(people), 1);
    const firstAssigneeName = isOneAssignee && personName(_.first(people));
    const shouldShowMoreActions = !_.isEmpty(actions);

    return (
      <div className={`${CLASS_NAME}__people-section`}>
        {_.map(firstPeople, this.renderAvatar)}
        {shouldShowMore && this.renderMorePeople(morePeople)}

        {!_.isEmpty(firstAssigneeName) && (
          <div className={`${CLASS_NAME}__assignee-name`}>{firstAssigneeName}</div>
        )}

        {_.isEmpty(people) && (
          <div className={`${CLASS_NAME}__empty-text`}>No default team members</div>
        )}

        <div className={`${CLASS_NAME}__actions-section`}>
          <Buttons.IconButton
            className={`${CLASS_NAME}__add`}
            iconName="plus"
            onClick={onAdd}
            size="icon-medium"
            theme="icon-subtle"
          />
          {shouldShowMoreActions && (
            <Buttons.QuickActionsButton
              actions={actions}
              className={`${CLASS_NAME}__more`}
              size="icon-medium"
              theme="icon-subtle"
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    const { className, icon, title } = this.props;

    return (
      <div ref={this.ref} className={classnames(CLASS_NAME, className)}>
        <div className={`${CLASS_NAME}__title-section`}>
          {!_.isEmpty(icon) && <Icon className={`${CLASS_NAME}__icon`} iconName={icon} />}
          {!_.isEmpty(title) && <div className={`${CLASS_NAME}__title`}>{title}</div>}
        </div>
        {this.renderPeopleSection()}
      </div>
    );
  }
}

AssigneesBar.propTypes = {
  actions: PropTypes.array,
  className: PropTypes.string,
  icon: PropTypes.string,
  name: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  people: PropTypes.array,
  title: PropTypes.string,
};

AssigneesBar.defaultProps = {
  className: '',
};

export default AssigneesBar;
